'use client';

import { useEffect, useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import type { UfdrChatSummary, UfdrContact, UfdrExportData, UfdrSectionStatus } from '@/shared/ufdr/legacy-types';
import {
  fetchProvenanceTreePage,
  type FileSummary,
  type TreeNodeResponse,
} from '@/shared/cors-provenance-api';
import { encodeEvidenceLockerStyleProxyUriSegment, nestedUfdrMemberPath } from '@/shared/functions';
import {
  assembleUfdrExportData,
  collectNestedUfdrTreeNodes,
  findSidecarNodes,
  nodeFileUrl,
  nodeContentUrls,
  chatThreadParts,
  mapChatThreadFile,
  mergeUfdrCollectedTree,
  prefixChildTreePath,
  UFDR_SIDECAR_KINDS,
  type UfdrSidecarKind,
} from '@/shared/ufdr-export-adapter';
import { useProvenanceSession } from './useProvenanceSession';

export type UfdrPreviewContext = {
  url: string;
  fileStatusId?: string | null;
  label?: string;
  fileInfo?: FileSummary | null;
  /** Locker ``data/bulk/…`` path. Used to match an acquisition. Not an ``s3://`` URL. */
  path?: string | null;
  /** Case folder segment (``folder_token`` or legacy name) for that match. */
  caseFolderName?: string | null;
  /** Set from `/mobile-viewer?acquisitionId=`. Skips the sidecar fetch. */
  acquisitionId?: number;
  artifactType?: string;
  view?: string;
  rowIds?: string[];
  threadId?: string;
  family?: string;
  search?: string;
  filters?: string;
  record?: string;
  page?: string;
};

const PAGE_SIZE = 200;
const MAX_PAGES = 15;
const MAX_DIR_FETCHES = 80;
const MAX_DEFERRED_DIR_FETCHES = 240;
const MAX_NODES = 8000;

type UfdrExportCache = {
  deferredPaths: string[];
  threadUrls: Record<string, string[]>;
};

const exportCache = new Map<string, UfdrExportCache>();

export function ufdrExportCache(fileStatusId: string): UfdrExportCache | undefined {
  return exportCache.get(fileStatusId);
}

async function fetchTreeListing(args: {
  cellId: string;
  accessToken: string;
  fileStatusId?: string;
  path?: string;
  signal?: AbortSignal;
}): Promise<TreeNodeResponse[]> {
  const items: TreeNodeResponse[] = [];
  let offset = 0;
  for (let page = 0; page < MAX_PAGES; page++) {
    const result = await fetchProvenanceTreePage({
      cellId: args.cellId,
      accessToken: args.accessToken,
      fileStatusId: args.fileStatusId,
      path: args.path,
      view: 'user',
      offset,
      limit: PAGE_SIZE,
      signal: args.signal,
    });
    items.push(...result.items);
    if (result.nextOffset == null) break;
    offset = result.nextOffset;
  }
  return items;
}

async function fetchSidecarText(
  urls: string | string[],
  signal?: AbortSignal,
): Promise<string | null> {
  const list = (Array.isArray(urls) ? urls : [urls]).filter(Boolean);
  for (const url of list) {
    const proxy = `/api/proxy/${encodeEvidenceLockerStyleProxyUriSegment(url)}`;
    const response = await fetch(proxy, { signal });
    if (!response.ok) continue;
    return response.text();
  }
  return null;
}

export async function loadUfdrExportData(args: {
  cellId: string;
  accessToken: string;
  context: UfdrPreviewContext;
  signal?: AbortSignal;
}): Promise<UfdrExportData> {
  const title =
    args.context.label ||
    args.context.fileInfo?.fileName ||
    args.context.url.split('/').pop() ||
    'Extraction';
  const fileStatusId = args.context.fileStatusId?.trim();
  if (!fileStatusId) {
    return assembleUfdrExportData({
      title,
      nodes: [],
      fileInfo: args.context.fileInfo,
    });
  }

  const skipSidecars = args.context.acquisitionId != null;
  const sidecars: Partial<Record<UfdrSidecarKind, string>> = {};
  const sidecarNames: Partial<Record<UfdrSidecarKind, string>> = {};
  const inflight = new Map<UfdrSidecarKind, Promise<void>>();

  const startSidecarFetches = (found: Partial<Record<UfdrSidecarKind, TreeNodeResponse>>) => {
    if (skipSidecars) return;
    for (const kind of UFDR_SIDECAR_KINDS) {
      const node = found[kind];
      if (!node || inflight.has(kind)) continue;
      const url = nodeFileUrl(node);
      if (!url) continue;
      sidecarNames[kind] = node.name || node.path;
      inflight.set(
        kind,
        fetchSidecarText(url, args.signal).then((text) => {
          if (text != null) sidecars[kind] = text;
        }),
      );
    }
  };

  const listDirectory = (path: string | undefined) =>
    fetchTreeListing({
      cellId: args.cellId,
      accessToken: args.accessToken,
      fileStatusId,
      path,
      signal: args.signal,
    });
  const looksLikeZip = /\.zip(?:$|[?#])/i.test(
    `${args.context.url} ${args.context.label ?? ''} ${args.context.fileInfo?.fileName ?? ''}`,
  );
  let rootPath: string | undefined;
  let rootListing: TreeNodeResponse[] = [];
  if (looksLikeZip && !skipSidecars) {
    rootListing = await listDirectory(undefined);
    rootPath = nestedUfdrMemberPath(rootListing);
  }
  const deferredPaths: string[] = [];
  // Keep walking after the sidecar files show up. messages.json is not the end
  // of the export: generated/mobile/threads and pdf/communication come later.
  // collected/<md5> stays queued behind those folders and pages afterward.
  const walked = await collectNestedUfdrTreeNodes({
    listDirectory,
    rootPath,
    maxDirFetches: MAX_DIR_FETCHES,
    maxNodes: MAX_NODES,
    deferredPaths,
    onProgress: (listed) => startSidecarFetches(findSidecarNodes(listed)),
  });
  const nodes = rootPath ? [...rootListing, ...walked] : walked;
  await Promise.all(inflight.values());
  const threadUrls: Record<string, string[]> = {};
  for (const node of nodes) {
    if (node.nodeType === 'directory') continue;
    const path = node.path || node.name;
    if (!path || !chatThreadParts(path)) continue;
    threadUrls[path] = nodeContentUrls(node);
  }
  exportCache.set(fileStatusId, { deferredPaths, threadUrls });

  return assembleUfdrExportData({
    title,
    nodes,
    fileInfo: args.context.fileInfo,
    sidecars: skipSidecars ? undefined : sidecars,
    callLogFilename: sidecarNames.callLog,
  });
}

export async function loadDeferredUfdrNodes(args: {
  cellId: string;
  accessToken: string;
  fileStatusId: string;
  signal?: AbortSignal;
}): Promise<TreeNodeResponse[]> {
  const queue = [...(exportCache.get(args.fileStatusId)?.deferredPaths ?? [])];
  const nodes: TreeNodeResponse[] = [];
  let fetches = 0;
  while (queue.length > 0 && fetches < MAX_DEFERRED_DIR_FETCHES && nodes.length < MAX_NODES) {
    const path = queue.shift();
    if (!path) break;
    fetches += 1;
    const items = await fetchTreeListing({
      cellId: args.cellId,
      accessToken: args.accessToken,
      fileStatusId: args.fileStatusId,
      path,
      signal: args.signal,
    });
    for (const item of items) {
      const prefixed = prefixChildTreePath(path, item);
      nodes.push(prefixed);
      if (prefixed.nodeType === 'directory' && prefixed.path) queue.push(prefixed.path);
    }
  }
  return nodes;
}

export async function loadUfdrChatThreadText(args: {
  fileStatusId: string;
  chatId: string;
  signal?: AbortSignal;
}): Promise<UfdrChatSummary | null> {
  const urls = exportCache.get(args.fileStatusId)?.threadUrls[args.chatId] ?? [];
  if (urls.length === 0) return null;
  const text = await fetchSidecarText(urls, args.signal);
  if (text == null) return null;
  return mapChatThreadFile({ path: args.chatId, text });
}

function mergeChatThread(data: UfdrExportData, chat: UfdrChatSummary): UfdrExportData {
  const chats = (data.chats ?? []).map((existing) =>
    existing.id === chat.id ? { ...existing, ...chat, id: existing.id } : existing,
  );
  return { ...data, chats };
}

export function useUfdrExportData(
  context: UfdrPreviewContext | null,
  enabled: boolean,
) {
  const { cellId, accessToken, enabled: sessionEnabled } = useProvenanceSession();
  const fileStatusId = context?.fileStatusId ?? '';
  const url = context?.url ?? '';
  const hasUrl = Boolean(url);
  // Citation links are `/mobile-viewer?acquisitionId=…` with an empty url.
  // The export query can run only when there is a url and a provenance session.
  const sessionReady = Boolean(enabled && sessionEnabled && hasUrl && cellId && accessToken);
  const noExportUrl = Boolean(enabled && context && !hasUrl);
  const routeThreadId = context?.threadId?.trim() || null;
  const [openChatId, setOpenChatId] = useState<string | null>(routeThreadId);
  useEffect(() => {
    setOpenChatId(routeThreadId);
  }, [routeThreadId]);

  const baseQuery = useQuery<UfdrExportData, Error>({
    queryKey: ['ufdr-export', cellId, fileStatusId, url],
    queryFn: ({ signal }) =>
      loadUfdrExportData({
        cellId: cellId as string,
        accessToken: accessToken as string,
        context: context as UfdrPreviewContext,
        signal,
      }),
    enabled: sessionReady,
    staleTime: 5 * 60 * 1000,
  });

  const deferred = useQuery<TreeNodeResponse[], Error>({
    queryKey: ['ufdr-export-deferred', cellId, fileStatusId],
    queryFn: ({ signal }) =>
      loadDeferredUfdrNodes({
        cellId: cellId as string,
        accessToken: accessToken as string,
        fileStatusId,
        signal,
      }),
    enabled: Boolean(sessionReady && baseQuery.isSuccess && (ufdrExportCache(fileStatusId)?.deferredPaths.length ?? 0) > 0),
    staleTime: 5 * 60 * 1000,
  });

  const selectedHasMessages = Boolean(
    openChatId && baseQuery.data?.chats?.find((chat) => chat.id === openChatId)?.messages?.length,
  );
  const thread = useQuery<UfdrChatSummary | null, Error>({
    queryKey: ['ufdr-chat-thread', cellId, fileStatusId, openChatId],
    queryFn: ({ signal }) =>
      loadUfdrChatThreadText({
        fileStatusId,
        chatId: openChatId as string,
        signal,
      }),
    enabled: Boolean(sessionReady && baseQuery.isSuccess && openChatId && !selectedHasMessages),
    staleTime: 5 * 60 * 1000,
  });

  const data = useMemo(() => {
    if (!baseQuery.data) return baseQuery.data;
    const withMedia = deferred.data?.length
      ? mergeUfdrCollectedTree(baseQuery.data, deferred.data)
      : baseQuery.data;
    return thread.data ? mergeChatThread(withMedia, thread.data) : withMedia;
  }, [baseQuery.data, deferred.data, thread.data]);

  const chatStatus: UfdrSectionStatus | undefined = thread.isFetching
    ? { status: 'loading' }
    : thread.isError
      ? { status: 'error', message: thread.error.message }
      : undefined;

  return {
    ...baseQuery,
    data: noExportUrl ? undefined : data,
    isPending: noExportUrl ? false : baseQuery.isPending,
    isLoading: noExportUrl ? false : baseQuery.isLoading,
    isFetching: noExportUrl ? false : baseQuery.isFetching,
    isFetched: noExportUrl ? true : baseQuery.isFetched,
    isSuccess: noExportUrl ? true : baseQuery.isSuccess,
    isError: noExportUrl ? false : baseQuery.isError,
    status: noExportUrl ? 'success' : baseQuery.status,
    fetchStatus: noExportUrl ? 'idle' : baseQuery.fetchStatus,
    error: noExportUrl ? null : baseQuery.error,
    chatStatus,
    onChatSelect: (chat: { id: string } | undefined) => setOpenChatId(chat?.id ?? null),
    onContactSelect: (_contact: UfdrContact | undefined) => undefined,
  };
}
